<template>
    <div>
        <div class="tw-flex tw-items-center tw-gap-4">
            <h1 class="tw-border-s-2 tw-border-solid tw-border-primary-500 tw-ps-2 tw-text-base">{{ $t('common.cross-products') }}</h1>
            <CreateCrossProductPopup @created="addCrossProduct" />
        </div>

        <div v-if="crossProducts.length === 0"
            class="tw-p-5 tw-text-center tw-bg-gray-50 tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-mt-4">
            <p>{{ $t('messages.table-no-results') }}</p>
        </div>

        <template v-else>
            <div
                class="tw-mt-4 tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
                <div class="tw-divide-y tw-divide-gray-100">
                    <div v-for="product in crossProducts" :key="product.id"
                        class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 hover:tw-bg-gray-50 transition">
                        <!-- Product Details -->
                        <div class="tw-flex tw-items-center tw-gap-4">
                            <div class="tw-flex tw-flex-col tw-min-w-[100px]">
                                <p class="tw-text-[10px] tw-text-gray-500">{{ $t('fields.product') }}</p>
                                <p class="tw-text-sm tw-font-semibold">{{ product.name }}</p>
                            </div>

                            <div class="tw-flex tw-flex-col tw-min-w-[60px]">
                                <p class="tw-text-[10px] tw-text-gray-500">{{ $t('fields.price') }}</p>
                                <div class="tw-flex tw-items-end tw-gap-1">
                                    <p class="tw-text-sm  tw-font-semibold">{{ product.price }}</p>
                                    <span class="tw-text-xs tw-text-gray-500">{{ CURRENCY }}</span>
                                </div>
                            </div>

                            <div class="tw-flex tw-flex-col tw-min-w-[100px]">
                                <p class="tw-text-[10px] tw-text-gray-500">{{ $t('fields.note') }}</p>
                                <p class="tw-text-sm tw-truncate">{{ product.note }}</p>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <UpdateCrossProductPopup :initial-data="product" @updated="updateCrossProduct" />
                            <a-button intent="danger" class="tw-text-sm tw-px-3 tw-py-1 !tw-min-h-[20px]" @click="deleteCrossProduct(product.id)">
                                {{ $t('buttons.delete') }}
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, onMounted, watch } from 'vue';
import CreateCrossProductPopup from './CreateCrossProductPopup.vue';
import UpdateCrossProductPopup from './UpdateCrossProductPopup.vue';
import { useStateStore } from '@/stores/app/state';
import { CURRENCY } from '@/config/app';

const stateStore = useStateStore();
const products = computed(() => stateStore.products);

const props = defineProps({
    cross: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:cross']);

const crossProducts = computed(() => props.cross);

// Update product names on initial load and when products or cross products change
const updateProductNames = () => {
    if (props.cross.length > 0) {
        let hasChanges = false;
        const updatedCross = props.cross.map(item => {
            // If name is missing or empty, try to set it from stateStore
            if (!item.name || item.name.trim() === '') {
                const foundProduct = products.value.find(p => p.id === item.cross_product_id);
                const productName = foundProduct?.name || 'Unknown';
                hasChanges = true;
                return { ...item, name: productName };
            }
            return item;
        });
        
        // Only emit if there were actual changes
        if (hasChanges) {
            emit('update:cross', updatedCross);
        }
    }
};

// Watch for changes in the products store or cross products
watch([() => products.value, () => props.cross], () => {
    // Only update if there are items that need names updated
    const needsUpdate = props.cross.some(item => 
        (!item.name || item.name.trim() === '') && 
        products.value.some(p => p.id === item.cross_product_id)
    );
    
    if (needsUpdate) {
        updateProductNames();
    }
}, { deep: true });

// Run on mount to ensure names are populated initially
onMounted(() => {
    updateProductNames();
});

const addCrossProduct = (product) => {
    const productName = products.value.find(p => p.id === product.cross_product_id)?.name || 'Unknown';
    const updatedCross = [...props.cross, { ...product, name: productName }];
    emit('update:cross', updatedCross);
};

const updateCrossProduct = (product) => {
    const updatedCross = [...props.cross];
    const index = updatedCross.findIndex(p => p.id === product.id);
    console.log('product: ', product);
    
    if (index !== -1) {
        const productName = products.value.find(p => p.id === product.cross_product_id)?.name || 'Unknown';
        updatedCross[index] = { ...product, name: productName };
        emit('update:cross', updatedCross);
    }
};

const deleteCrossProduct = (id) => {
    console.log('Attempting to delete cross product with ID:', id);
    console.log('Current cross products:', JSON.stringify(props.cross));
    
    // Make sure id is a string for consistent comparison
    const stringId = String(id);
    
    const updatedCross = props.cross.filter(product => {
        const productId = String(product.id);
        const keepItem = productId !== stringId;
        console.log('Product ID:', productId, 'Keep:', keepItem);
        return keepItem;
    });
    
    console.log('Cross products after deletion:', JSON.stringify(updatedCross));
    
    // Ensure we're emitting a new array reference
    emit('update:cross', [...updatedCross]);
    
    // Force update the parent's cross array after a small delay to ensure reactivity
    setTimeout(() => {
        if (props.cross.some(p => String(p.id) === stringId)) {
            console.log('Item still exists after deletion, forcing update');
            const forcedUpdate = props.cross.filter(p => String(p.id) !== stringId);
            emit('update:cross', [...forcedUpdate]);
        }
    }, 50);
};
</script>

<style></style>